import { Link } from 'react-router-dom';
import { IDS } from '@/constants/testIds';
import { Check } from 'lucide-react';
import PriceBlock from './PriceBlock';
import AddToCatalogButton from './AddToCatalogButton';

const IJE_FALLBACK = `data:image/svg+xml;utf8,${encodeURIComponent(`<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 400'><defs><linearGradient id='g' x1='0' y1='0' x2='1' y2='1'><stop offset='0' stop-color='#FBF0F6'/><stop offset='1' stop-color='#C2478A'/></linearGradient></defs><rect width='400' height='400' fill='url(#g)'/><text x='200' y='215' text-anchor='middle' font-family='Georgia, serif' font-style='italic' font-size='90' fill='#0A0A0A' opacity='0.4'>IJE</text></svg>`)}`;

function StockBadge({ product, compact }) {
  const inStock = product.stock_status === 'in_stock';
  const cls = compact ? 'badge badge--sm' : 'badge';
  if (inStock) return <span className={`${cls} badge--in-stock`} data-testid="stock-in">In Stock</span>;
  if (product.stock_status === 'out_of_stock_coming_soon') return <span className={`${cls} badge--soon`} data-testid="stock-out">Out</span>;
  return <span className={`${cls} badge--out`}>Unavailable</span>;
}

// `compact` collapses the card into a denser 2-col mobile tile.
// `selectMode`/`selected`/`onToggle` drive the bulk-add checkboxes.
// `onLongPress` (optional) fires after a 500ms hold — the Catalog page uses
// it to auto-enter select mode with the pressed card pre-selected.
export default function ProductCard({ product, compact = false, selectMode = false, selected = false, onToggle, onLongPress }) {
  const raw = (product.images && product.images[0]) || '';
  // Card view can use the smaller thumbnail variant served by the backend.
  // Only apply to our own uploads (`/api/images/...`) — external DJ URLs stay untouched.
  const img = raw
    ? (raw.startsWith('/api/images/') ? `${raw}${raw.includes('?') ? '&' : '?'}thumb=1` : raw)
    : IJE_FALLBACK;

  const longPressState = { timer: null, fired: false };
  const startPress = () => {
    if (!onLongPress || selectMode) return;
    longPressState.fired = false;
    longPressState.timer = setTimeout(() => {
      longPressState.fired = true;
      onLongPress(product.sku);
    }, 500);
  };
  const endPress = () => {
    if (longPressState.timer) clearTimeout(longPressState.timer);
    longPressState.timer = null;
  };
  const cancelPress = () => {
    if (longPressState.timer) clearTimeout(longPressState.timer);
    longPressState.timer = null;
    longPressState.fired = false;
  };
  const handleClick = (e) => {
    if (longPressState.fired) { e.preventDefault(); e.stopPropagation(); longPressState.fired = false; }
  };

  const inner = (
    <>
      {selectMode ? (
        <span
          className={`absolute top-2 left-2 z-10 w-7 h-7 rounded-full border-2 flex items-center justify-center transition-colors ${selected ? 'bg-[color:var(--ije-pink)] border-[color:var(--ije-pink)]' : 'bg-white/90 border-neutral-300'}`}
          data-testid={`product-select-${product.sku}`}
          aria-checked={selected}
          role="checkbox"
        >
          {selected && <Check size={14} className="text-white" strokeWidth={3} />}
        </span>
      ) : (
        <AddToCatalogButton sku={product.sku} compact />
      )}
      <div className="product-card__image">
        <img src={img} alt={product.name} loading="lazy" referrerPolicy="no-referrer" />
      </div>
      <div className={`${compact ? 'p-3 gap-1.5' : 'p-5 gap-2'} flex flex-col flex-1`}>
        <div className="flex items-center justify-between gap-2">
          <span className={`eyebrow ${compact ? 'text-[9px]' : ''} truncate`}>{product.brand}</span>
          <StockBadge product={product} compact={compact} />
        </div>
        <h3 className={`serif ${compact ? 'text-sm leading-tight line-clamp-2' : 'text-lg leading-snug'}`}>
          {product.name}
        </h3>
        {!compact && (
          <p className="sans text-xs text-neutral-500 tracking-widest uppercase">
            {product.sku}{product.variant ? ` · ${product.variant}` : ''}
          </p>
        )}
        <div className={compact ? 'mt-auto pt-2' : 'mt-auto pt-3'}>
          <PriceBlock product={product} size={compact ? 'compact' : 'card'} />
          {!compact && (
            <p className="sans text-[0.65rem] text-neutral-500 uppercase tracking-[0.2em] mt-3">
              Case of {product.case_pack_size} · MOQ {product.min_order_qty}
            </p>
          )}
        </div>
      </div>
    </>
  );

  if (selectMode) {
    return (
      <button
        type="button"
        onClick={() => onToggle?.(product.sku)}
        className={`product-card relative text-left ${selected ? 'ring-2 ring-[color:var(--ije-pink)]' : ''}`}
        data-testid={IDS.catalog.productCard(product.id)}
      >
        {inner}
      </button>
    );
  }

  return (
    <Link
      to={`/products/${encodeURIComponent(product.sku)}`}
      className="product-card relative"
      data-testid={IDS.catalog.productCard(product.id)}
      onPointerDown={startPress}
      onPointerUp={endPress}
      onPointerCancel={cancelPress}
      onPointerLeave={cancelPress}
      onClick={handleClick}
    >
      {inner}
    </Link>
  );
}
